import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, BedDouble, Sofa, Bath, Ruler, CheckCircle, User, Calendar, MessageCircle, ChevronLeft, ChevronRight, Share2, ShieldCheck } from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import { supabase } from '../lib/supabase';

const PropertyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [property, setProperty] = useState<any | null>(null);
  const [similarProperties, setSimilarProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    fetchProperty();
    window.scrollTo(0, 0);
  }, [id]);

  const fetchProperty = async () => {
    setLoading(true);
    try {
      // 1. Récupérer le bien actuel
      const { data, error } = await supabase
        .from('biens')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setProperty(data);

      // 2. Récupérer des biens similaires (même type, mais pas le même ID)
      const { data: similar, error: similarError } = await supabase
        .from('biens')
        .select('*')
        .eq('type', data.type)
        .neq('id', id)
        .limit(3);

      if (!similarError) setSimilarProperties(similar || []);

    } catch (err) {
      console.error("Erreur lors de la récupération du bien:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-orange border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-navy font-medium">Chargement des détails...</p>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-center px-4">
        <div>
          <h2 className="text-2xl font-bold text-navy mb-4">Bien introuvable</h2>
          <Link to="/nos-biens" className="text-orange font-bold hover:underline">Retourner aux annonces</Link>
        </div>
      </div>
    );
  }

  // --- MAPPING DES DONNÉES ---
  const photos = Array.isArray(property.photos) ? property.photos : [];
  const displayPhotos = photos.length > 0 ? photos : ["https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1200&q=80"];

  const formatPrice = (price: number, type: string) => {
    const formatted = new Intl.NumberFormat('fr-BJ').format(price);
    return type === 'Vente-Achat' ? `${formatted} FCFA` : `${formatted} FCFA/mois`;
  };

  const nextImage = () => setActiveImageIndex((prev) => (prev + 1) % displayPhotos.length);
  const prevImage = () => setActiveImageIndex((prev) => (prev - 1 + displayPhotos.length) % displayPhotos.length);

  const whatsappMessage = `Bonjour LDF, je suis intéressé par le bien "${property.titre}" (Réf: ${property.id}) vu sur votre plateforme. Est-il toujours disponible ?`;
  const whatsappLink = `https://wa.me/22997406767?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div className="bg-gray-50 min-h-screen pb-24 md:pb-10">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4 text-sm text-gray-500">
          <Link to="/" className="hover:text-orange transition">Accueil</Link> / 
          <Link to="/nos-biens" className="hover:text-orange transition ml-1">Nos Biens</Link> / 
          <span className="ml-1 text-navy font-medium truncate">{property.titre}</span>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Gallery */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-100">
              <div className="relative aspect-video bg-gray-200 group">
                <img 
                  src={displayPhotos[activeImageIndex]} 
                  alt={property.titre} 
                  className="w-full h-full object-cover"
                />
                
                {displayPhotos.length > 1 && (
                  <>
                    <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/70 backdrop-blur-sm text-white p-3 rounded-full transition opacity-0 group-hover:opacity-100">
                      <ChevronLeft size={24} />
                    </button>
                    <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/70 backdrop-blur-sm text-white p-3 rounded-full transition opacity-0 group-hover:opacity-100">
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
                
                <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-md text-white px-3 py-1 rounded-full text-sm font-medium">
                  {activeImageIndex + 1} / {displayPhotos.length}
                </div>
              </div>
              
              <div className="p-4 flex gap-3 overflow-x-auto scrollbar-hide">
                {displayPhotos.map((img, idx) => (
                  <button 
                    key={idx}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`relative w-24 h-16 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${activeImageIndex === idx ? 'border-orange scale-105' : 'border-transparent opacity-70 hover:opacity-100'}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {/* Title & Price Header */}
            <div className="bg-white rounded-3xl shadow-lg p-8 border border-gray-100">
              <div className="flex flex-col md:flex-row justify-between md:items-start gap-4 mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`px-3 py-1 rounded-lg text-sm font-bold uppercase tracking-wide text-white
                        ${property.type === 'Location' ? 'bg-blue-600' : 
                          property.type === 'Meublé' ? 'bg-purple-600' : 'bg-orange'}`}>
                      {property.type}
                    </span>
                    <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg text-sm font-medium">
                        {property.categorie}
                    </span>
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold text-navy mb-2 leading-tight">{property.titre}</h1>
                  <div className="flex items-center text-gray-600">
                    <MapPin size={18} className="mr-1 text-orange" />
                    {property.zone}, {property.adresse}
                  </div>
                </div>
                <div className="text-left md:text-right">
                  <div className="text-3xl font-bold text-navy">
                    {formatPrice(property.prix, property.type)}
                  </div>
                  <div className="text-sm text-gray-500 mt-1 flex items-center md:justify-end">
                      <Calendar size={14} className="mr-1" />
                      Réf: {property.id.slice(0, 8)}
                  </div>
                </div>
              </div>

              {/* Stats Bar */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6 border-t border-b border-gray-100">
                  <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-2xl">
                    <BedDouble size={24} className="text-navy mb-2"/>
                    <span className="font-bold text-navy text-lg">{property.chambres}</span>
                    <span className="text-xs text-gray-500">Chambres</span>
                  </div>
                  <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-2xl">
                    <Sofa size={24} className="text-navy mb-2"/>
                    <span className="font-bold text-navy text-lg">{property.salons}</span>
                    <span className="text-xs text-gray-500">Salons</span>
                  </div>
                  <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-2xl">
                     <Bath size={24} className="text-navy mb-2"/>
                    <span className="font-bold text-navy text-lg">{property.douches}</span>
                    <span className="text-xs text-gray-500">Douches</span>
                  </div>
                  <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-2xl">
                    <Ruler size={24} className="text-navy mb-2"/>
                    <span className="font-bold text-navy text-lg">{property.surface} m²</span>
                    <span className="text-xs text-gray-500">Surface</span>
                  </div>
              </div>

              <div className="mt-6">
                 <h2 className="text-xl font-bold text-navy mb-4">Description</h2>
                 <div className="prose text-gray-600 leading-relaxed whitespace-pre-line">
                    {property.description}
                 </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-4">
                {property.meuble && (
                  <div className="inline-flex items-center px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium border border-green-100">
                    <CheckCircle size={16} className="mr-2" /> Meublé / Équipé
                  </div>
                )}
                {property.disponible ? (
                   <div className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-100">
                    <CheckCircle size={16} className="mr-2" /> Actuellement disponible
                  </div>
                ) : (
                  <div className="inline-flex items-center px-4 py-2 bg-red-50 text-red-700 rounded-full text-sm font-medium border border-red-100">
                    Indisponible
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-3xl shadow-lg p-6 border border-gray-100 sticky top-24">
              <h3 className="text-xl font-bold text-navy mb-6">Contactez-nous</h3>
              <div className="flex items-center gap-4 mb-8">
                <div className="bg-navy p-3 rounded-2xl text-white">
                  <User size={32} />
                </div>
                <div>
                  <div className="font-bold text-lg text-navy">LDF Bénin</div>
                  <div className="text-sm text-gray-500">Service Client</div>
                  <div className="text-xs text-green-600 mt-1 font-medium flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></div>
                      Agent disponible
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <a 
                  href={whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-4 px-4 rounded-xl flex items-center justify-center transition shadow-lg transform hover:-translate-y-1"
                >
                  <MessageCircle size={22} className="mr-2" />
                  Discuter sur WhatsApp
                </a>
                
                <hr className="border-gray-100" />
                
                <button className="w-full bg-white border border-gray-200 text-gray-700 font-medium py-3 px-4 rounded-xl flex items-center justify-center hover:bg-gray-50 transition">
                  <Share2 size={18} className="mr-2" />
                  Partager l'annonce
                </button>
              </div>
            </div>

            <div className="bg-blue-50/50 rounded-3xl p-6 border border-blue-100">
              <h3 className="font-bold text-navy mb-4 flex items-center">
                <ShieldCheck size={20} className="mr-2 text-orange"/> 
                Garantie LDF
              </h3>
              <ul className="text-sm text-gray-600 space-y-3">
                <li className="flex items-start">
                    <span className="text-orange mr-2">•</span>
                    Bien vérifié par nos agents.
                </li>
                <li className="flex items-start">
                    <span className="text-orange mr-2">•</span>
                    Accompagnement juridique.
                </li>
                <li className="flex items-start">
                    <span className="text-orange mr-2">•</span>
                    Zéro frais avant visite.
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Similar Properties */}
        {similarProperties.length > 0 && (
          <div className="mt-20">
            <div className="flex items-center gap-4 mb-8">
                <h2 className="text-2xl font-bold text-navy">Biens similaires</h2>
                <div className="h-px bg-gray-200 flex-1"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {similarProperties.map(p => (
                <PropertyCard key={p.id} property={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyDetail;
